import React, { useEffect } from 'react';
import {
  CpuChipIcon,
  CircleStackIcon,
  ServerIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { useMonitor } from '../hooks/useMonitor';

/**
 * Component to display system monitoring information
 */
export function MonitorPanel() {
  const { health, loading, error, getSystemHealth } = useMonitor();

  // Format bytes to more readable units (MB, GB, TB)
  const formatBytes = (mb) => {
    if (mb >= 1024 * 1024) {
      // TB
      return `${(mb / (1024 * 1024)).toFixed(2)} TB`;
    } else if (mb >= 1024) {
      // GB
      return `${(mb / 1024).toFixed(2)} GB`;
    } else {
      // MB
      return `${mb.toFixed(2)} MB`;
    }
  };

  useEffect(() => {
    // Get initial data
    getSystemHealth();

    // Refresh every 5 seconds
    const interval = setInterval(getSystemHealth, 5000);
    return () => clearInterval(interval);
  }, [getSystemHealth]);

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'bg-red-500';
    if (percentage >= 60) return 'bg-orange-500';
    if (percentage >= 40) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getProgressBgColor = (percentage) => {
    if (percentage >= 80) return 'bg-red-100';
    if (percentage >= 60) return 'bg-orange-100';
    if (percentage >= 40) return 'bg-yellow-100';
    return 'bg-green-100';
  };

  const getStatusBadge = (percentage) => {
    if (percentage >= 80) return { label: 'Crítico', color: 'text-red-700' };
    if (percentage >= 60) return { label: 'Alto', color: 'text-orange-700' };
    if (percentage >= 40) return { label: 'Médio', color: 'text-yellow-700' };
    return { label: 'Normal', color: 'text-green-700' };
  };

  if (loading && !health) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Carregando informações do sistema...</p>
        </div>
      </div>
    );
  }

  if (error && !health) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">Erro ao carregar dados: {error}</p>
      </div>
    );
  }

  if (!health) return null;

  const cpuStatus = getStatusBadge(health.cpu.usage);
  const memoryStatus = getStatusBadge(health.memory.percentage);
  const diskStatus = getStatusBadge(health.disk.percentage);

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Visão geral</h2>
        <button
          onClick={getSystemHealth}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition"
        >
          <ArrowPathIcon className="w-5 h-5" />
          Atualizar
        </button>
      </div>

      {/* System Info */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-gray-600">Servidor</p>
            <p className="font-semibold text-gray-900">{health.hostname}</p>
          </div>
          <div>
            <p className="text-gray-600">Sistema Operacional</p>
            <p className="font-semibold text-gray-900 capitalize">{health.platform} ({health.arch})</p>
          </div>
          <div>
            <p className="text-gray-600">Processadores</p>
            <p className="font-semibold text-gray-900">{health.cpus} CPUs</p>
          </div>
          <div>
            <p className="text-gray-600">Uptime</p>
            <p className="font-semibold text-gray-900">{health.uptime.formatted}</p>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* CPU Usage */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <CpuChipIcon className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-gray-600 text-sm">Uso da CPU</p>
                <p className={`text-2xl font-bold ${cpuStatus.color}`}>{health.cpu.usage.toFixed(1)}%</p>
              </div>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${cpuStatus.color} ${getProgressBgColor(health.cpu.usage)}`}>
              {cpuStatus.label}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(health.cpu.usage)}`}
              style={{ width: `${Math.min(health.cpu.usage, 100)}%` }}
            ></div>
          </div>
        </div>

        {/* Memory Usage */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <CircleStackIcon className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-gray-600 text-sm">Uso da Memória</p>
                <p className={`text-2xl font-bold ${memoryStatus.color}`}>{health.memory.percentage.toFixed(1)}%</p>
              </div>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${memoryStatus.color} ${getProgressBgColor(health.memory.percentage)}`}>
              {memoryStatus.label}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(health.memory.percentage)}`}
              style={{ width: `${Math.min(health.memory.percentage, 100)}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {health.memory.used.toFixed(2)} GB de {health.memory.total.toFixed(2)} GB
          </p>
        </div>

        {/* Disk Usage */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <ServerIcon className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-gray-600 text-sm">Uso do Disco</p>
                <p className={`text-2xl font-bold ${diskStatus.color}`}>{health.disk.percentage.toFixed(1)}%</p>
              </div>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${diskStatus.color} ${getProgressBgColor(health.disk.percentage)}`}>
              {diskStatus.label}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(health.disk.percentage)}`}
              style={{ width: `${Math.min(health.disk.percentage, 100)}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {health.disk.used.toFixed(2)} GB de {health.disk.total.toFixed(2)} GB
          </p>
        </div>
      </div>

      {/* Network Traffic */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Tráfego de Entrada</h3>
          <p className="text-3xl font-bold text-blue-600">{formatBytes(health.network.bytesIn)}</p>
          <p className="text-sm text-gray-500 mt-2">Total recebido</p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Tráfego de Saída</h3>
          <p className="text-3xl font-bold text-green-600">{formatBytes(health.network.bytesOut)}</p>
          <p className="text-sm text-gray-500 mt-2">Total enviado</p>
        </div>
      </div>

      {/* Last Updated */}
      <p className="text-xs text-gray-500 text-center">
        Última atualização: {new Date(health.timestamp).toLocaleTimeString('pt-BR')}
      </p>
    </div>
  );
}
